import type { PhoneE164 } from "@event-manager/api-client";
import type {
	DynamicRouteKey,
	DynamicRouteRegistry,
	RouteArgs,
	RouteParams,
	RouteRegistry,
	StaticRouteKey,
	StaticRouteRegistry,
} from "./route-types";

/**
 * Centralized route registry with type-safe parameters
 *
 * Usage:
 * - routes.calendar -> '/calendar'
 * - routes.chat({ id: '123' }) -> '/chat/123'
 * - routes.documentsByUser({ phoneE164: '+1234567890' as PhoneE164, userName: 'John' }) -> '/documents?phoneE164=%2B1234567890&userName=John'
 */
const staticRoutes = {
	// Core routes
	dashboard: "/dashboard",
	config: "/config",
	contacts: "/contacts",
	campaigns: "/campaigns",
	waitlist: "/waitlist",
	docs: "/docs",
	account: "/account",
	admin: "/superuser",
	billing: "/billing",
	roles: "/roles",
	members: "/members",
	org: "/org",
	debug: "/debug",
	playground: "/playground",

	// Document routes
	documents: "/documents",

	// Calendar routes
	calendar: "/calendar",

	// User routes
	userSettings: "/settings",

	// Auth routes
	login: "/login",
	register: "/register",
	forgotPassword: "/forgot-password",
} as const satisfies StaticRouteRegistry;

const dynamicRoutes = {
	// Chat routes
	chat: (params: { id: string }) => `/chat/${params.id}`,

	// Document routes
	documentsByUser: (params: {
		phoneE164: PhoneE164;
		userName?: string;
	}): string => {
		const base = `/documents?phoneE164=${encodeURIComponent(params.phoneE164)}`;
		if (params.userName) {
			return `${base}&userName=${encodeURIComponent(params.userName)}`;
		}
		return base;
	},

	// Calendar routes
	calendarWithDate: (params: { date: string }) =>
		`/calendar?date=${encodeURIComponent(params.date)}`,

	// User routes
	userProfile: (params: { userId: string }) => `/user/${params.userId}`,

	// Auth routes
	loginWithMode: (params: { mode: "signup" | "signin" }) =>
		`/login?mode=${params.mode}`,
} as const satisfies DynamicRouteRegistry;

export const routes = {
	...staticRoutes,
	...dynamicRoutes,
} as const satisfies RouteRegistry;

const dynamicRouteKeys: ReadonlySet<keyof RouteParams> = new Set(
	Object.keys(dynamicRoutes) as DynamicRouteKey[],
);

export function isDynamicRoute(
	route: keyof RouteParams,
): route is DynamicRouteKey {
	return dynamicRouteKeys.has(route);
}

/**
 * Type-safe route getter
 */
export function getRoute<T extends StaticRouteKey>(route: T): string;
export function getRoute<T extends DynamicRouteKey>(
	route: T,
	params: RouteParams[T],
): string;
export function getRoute(...args: RouteArgs): string;
export function getRoute(...args: RouteArgs): string {
	switch (args[0]) {
		case "chat":
			return dynamicRoutes.chat(args[1]);
		case "documentsByUser":
			return dynamicRoutes.documentsByUser(args[1]);
		case "calendarWithDate":
			return dynamicRoutes.calendarWithDate(args[1]);
		case "userProfile":
			return dynamicRoutes.userProfile(args[1]);
		case "loginWithMode":
			return dynamicRoutes.loginWithMode(args[1]);
		default:
			return staticRoutes[args[0]];
	}
}

/**
 * Check if a path matches a route pattern
 */
export function matchesRoute(path: string, route: keyof RouteParams): boolean {
	const routeValue = routes[route];
	if (typeof routeValue === "string") {
		return path === routeValue;
	}
	// For dynamic routes, check prefix
	const prefix = routeValue.toString().split("/")[1];
	return path.startsWith(`/${prefix}`);
}
